import { ref, watch } from 'vue'
import type { Schemas } from '#shopware'
import type { WarrantyLabelData } from './useProductWarrantyLabel'
import { extractWarrantyDataFromProduct } from '~/utils/warrantyLabel'
import { useCart, useShopwareContext } from '#imports'

/**
 * Composable for fetching warranty label data for cart line items
 * Bulk-loads product data for all line items to avoid N+1 queries
 */
export function useCartWarrantyLabels() {
    const { cart } = useCart()
    const { apiClient } = useShopwareContext()

    const warrantyDataMap = ref<Map<string, WarrantyLabelData>>(new Map())
    const isLoading = ref(false)

    async function loadWarrantyData() {
        if (!cart.value?.lineItems) return

        const productLineItems = cart.value.lineItems.filter(
            (item) => item.type === 'product' && item.referencedId
        )

        if (productLineItems.length === 0) return

        const productIds = productLineItems
            .map((item) => item.referencedId)
            .filter((id): id is string => id != null)

        if (productIds.length === 0) return

        isLoading.value = true

        try {
            const { data } = await apiClient.invoke('readProduct post /product', {
                body: {
                    ids: productIds,
                    includes: {
                        product: [
                            'id',
                            'productNumber',
                            'ean',
                            'manufacturerNumber',
                            'translated',
                            'manufacturer',
                            'customFields.custom_a4g_warranty_label_product_years',
                            'customFields.custom_a4g_warranty_label_product_hide',
                        ],
                        product_manufacturer: [
                            'id',
                            'name',
                            'translated',
                            'customFields.custom_a4g_warranty_label_years',
                        ],
                    },
                    associations: {
                        manufacturer: {},
                    },
                },
            })

            const newMap = new Map<string, WarrantyLabelData>()

            for (const product of (data.elements || [])) {
                const productData = product as Schemas['Product']

                if (!productData.manufacturer || !productData.id) continue

                const warrantyData = extractWarrantyDataFromProduct(productData, productData.manufacturer)
                if (warrantyData) {
                    newMap.set(productData.id, warrantyData)
                }
            }

            warrantyDataMap.value = newMap
        } catch (error) {
            console.error('Failed to load warranty data for cart items:', error)
        } finally {
            isLoading.value = false
        }
    }

    watch(() => cart.value?.lineItems, () => {
        loadWarrantyData()
    }, { immediate: true })

    function getWarrantyData(productId: string | null | undefined): WarrantyLabelData | null {
        if (!productId) return null
        return warrantyDataMap.value.get(productId) || null
    }

    return {
        warrantyDataMap,
        isLoading,
        getWarrantyData,
        loadWarrantyData,
    }
}
